/**
 * Jornada de campo por encuestador y día.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * «CUÁNTO TIEMPO ESTUVO EN CALLE» NO ES UN DATO, ES UNA ESTIMACIÓN
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Nadie ficha. Lo que el sistema sabe son MARCAS DE TIEMPO sueltas: cuándo
 * empezó y terminó cada entrevista, y cuándo se tocó cada puerta —incluidas
 * las que no abrieron—. De ahí se puede acotar la jornada, no medirla.
 *
 * Por eso se devuelven TRES cifras y no una, porque una sola daría una
 * precisión que el dato no tiene:
 *
 *   jornada    de la primera marca a la última. Es el TECHO: incluye la
 *              comida, los traslados largos y cualquier pausa.
 *   entrevistas la suma de las duraciones. Es el PISO: deja fuera caminar,
 *              tocar puertas que no abren, esperar y las revisitas.
 *   activo     la suma de los huecos entre marcas consecutivas, contando
 *              como mucho `MAX_GAP_MINUTES` por hueco. Es la estimación
 *              intermedia, y la única con un supuesto dentro.
 *
 * El supuesto está declarado a propósito en una constante con nombre: un
 * hueco de dos horas casi seguro es comida, y uno de media hora casi seguro
 * es caminar a la siguiente manzana. El corte decide a cuál se parece cada
 * hueco, y moverlo cambia la cifra. Quien lo mueva debe saber que lo hace.
 */
/**
 * Hueco máximo que se cuenta como trabajo.
 *
 * §9.1 planea 6–7 entrevistas efectivas por día, que a ~18 minutos son unas
 * dos horas de entrevista dentro de una jornada mucho más larga: el resto es
 * caminar, tocar y esperar. Así que los huecos grandes son normales y no se
 * pueden descartar sin más.
 *
 * 45 minutos es el corte: por debajo se parece a desplazarse dentro del
 * conglomerado —las manzanas de Tecate tienen menos de 90 m de lado— y por
 * encima se parece a una comida o a un traslado entre secciones, que no es
 * tiempo de puerta.
 */
export const MAX_GAP_MINUTES = 45;
/**
 * Zona horaria del estudio.
 *
 * NO es un detalle de presentación. El servidor corre en UTC y Tecate está
 * en UTC−7, así que agrupar por el día del servidor hace que cada «día» vaya
 * de las 17:00 locales a las 16:59 del siguiente: mezcla el final de una
 * tarde con la mañana de la otra y produce jornadas de 1,500 minutos.
 *
 * Se midió en producción: ENC07 aparecía con 74 h en cinco días —casi 15
 * diarias— y los días reales eran de una a cuatro horas.
 */
export const STUDY_TIMEZONE = 'America/Tijuana';
/**
 * Día natural de Tecate en formato ISO corto.
 *
 * `en-CA` da directamente AAAA-MM-DD, que ordena bien como texto.
 */
export function studyDay(at) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: STUDY_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(at);
}
/**
 * Acota la jornada a partir de las marcas de un día.
 *
 * Con una sola marca no hay jornada que medir: una entrevista aislada da
 * `span` 0 aunque el encuestador estuviera fuera toda la tarde. Se devuelve
 * tal cual en lugar de inventar un mínimo, porque un cero evidente invita a
 * mirar el dato y un número inventado no.
 */
export function estimateDay(events, interviewMinutes) {
    if (events.length === 0) {
        return {
            firstAt: null,
            lastAt: null,
            spanMinutes: 0,
            interviewMinutes: 0,
            activeMinutes: 0,
            cappedGaps: 0,
        };
    }
    const ordenados = [...events].sort((a, b) => a.at.getTime() - b.at.getTime());
    const first = ordenados[0].at;
    const last = ordenados[ordenados.length - 1].at;
    const spanMinutes = (last.getTime() - first.getTime()) / 60000;
    let activeMinutes = 0;
    let cappedGaps = 0;
    for (let i = 1; i < ordenados.length; i++) {
        const hueco = (ordenados[i].at.getTime() - ordenados[i - 1].at.getTime()) / 60000;
        if (hueco > MAX_GAP_MINUTES) {
            cappedGaps++;
            activeMinutes += MAX_GAP_MINUTES;
        }
        else {
            activeMinutes += hueco;
        }
    }
    return {
        firstAt: first,
        lastAt: last,
        spanMinutes: Math.round(spanMinutes),
        interviewMinutes: Math.round(interviewMinutes),
        // El activo nunca puede quedar por debajo del tiempo de entrevista: sería
        // decir que estuvo menos tiempo trabajando que entrevistando.
        activeMinutes: Math.round(Math.max(activeMinutes, interviewMinutes)),
        cappedGaps,
    };
}
/**
 * Entrevistas completas por hora de jornada.
 *
 * Se calcula sobre la jornada y no sobre el tiempo activo porque es la cifra
 * que se puede comparar entre personas sin arrastrar el supuesto del corte de
 * huecos. Devuelve nulo cuando la jornada es cero, que es lo que pasa con un
 * único registro en el día: dividir ahí daría infinito.
 */
export function interviewsPerHour(completed, spanMinutes) {
    if (spanMinutes <= 0)
        return null;
    return completed / (spanMinutes / 60);
}
/**
 * Proporción de puertas que terminaron en entrevista completa.
 *
 * NO es la tasa de respuesta AAPOR de §9.4: esa tiene su propia fórmula con
 * un tratamiento específico de los casos de elegibilidad desconocida. Esto es
 * un indicador operativo de productividad, y llamarlo de otro modo evitaría
 * que se cite como si fuera la otra.
 */
export function doorSuccessRate(completed, doors) {
    if (doors <= 0)
        return null;
    return completed / doors;
}
//# sourceMappingURL=workload.js.map